import { c as _c } from "react/compiler-runtime";
import { bn } from "@opensea/utils";
import { CandlestickSeries, HistogramSeries } from "lightweight-charts";
import { useEffect, useMemo, useState } from "react";
import { useOnChange } from "../../../../hooks";
import { formatPrice } from "../util/price";

// -- Types ----------------------------------------------------------------------------------------

// -- Hook -----------------------------------------------------------------------------------------
export function useInitializeCandlestickChart(t0) {
  const $ = _c(20);
  const {
    chart,
    data,
    initialTimeRange,
    onTimeRangeChange
  } = t0;
  const [initialized, setInitialized] = useState(false);
  const [candlestickSeries, setCandlestickSeries] = useState(null);
  const [volumeSeries, setVolumeSeries] = useState(null);
  let t1;
  let t2;
  if ($[0] !== data) {
    t2 = data.map(_temp);
    $[0] = data;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  t1 = t2;
  const formattedData = t1;
  const getVolumeData = _temp3;
  let t3;
  let t4;
  if ($[2] !== chart || $[3] !== formattedData || $[4] !== initialTimeRange || $[5] !== initialized) {
    t3 = () => {
      if (chart && !initialized) {
        const createdCandlestickSeries = chart.addSeries(CandlestickSeries, {
          upColor: "#47BB64",
          downColor: "#E24756",
          borderVisible: false,
          wickUpColor: "#47BB64",
          wickDownColor: "#E24756",
          priceLineVisible: true,
          lastValueVisible: true
        });
        const createdVolumeSeries = chart.addSeries(HistogramSeries, {
          priceFormat: {
            type: "volume"
          },
          priceScaleId: "",
          lastValueVisible: false,
          priceLineVisible: false
        });
        createdVolumeSeries.priceScale().applyOptions({
          scaleMargins: {
            top: 0.81,
            bottom: 0
          }
        });
        createdCandlestickSeries.applyOptions({
          priceFormat: {
            type: "custom",
            minMove: 1e-17,
            formatter: tradingViewSafePriceFormatter
          }
        });
        createdCandlestickSeries.setData(formattedData);
        createdVolumeSeries.setData(getVolumeData(formattedData));
        if (initialTimeRange) {
          chart.timeScale().setVisibleRange({
            from: initialTimeRange.startTime.getTime() / 1000,
            to: initialTimeRange.endTime.getTime() / 1000
          });
        } else {
          chart.timeScale().fitContent();
        }
        setCandlestickSeries(createdCandlestickSeries);
        setVolumeSeries(createdVolumeSeries);
        setInitialized(true);
      }
    };
    t4 = [initialized, chart, "#47BB64", "#E24756", formattedData, getVolumeData, initialTimeRange];
    $[2] = chart;
    $[3] = formattedData;
    $[4] = initialTimeRange;
    $[5] = initialized;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== chart || $[9] !== initialized || $[10] !== onTimeRangeChange) {
    t5 = () => {
      if (chart && initialized) {
        const handleVisibleTimeRangeChange = params => {
          if (!params) {
            return;
          }
          if (typeof params.from === "number" && typeof params.to === "number") {
            onTimeRangeChange?.(new Date(params.from * 1000), new Date(params.to * 1000));
          }
        };
        chart.timeScale().subscribeVisibleTimeRangeChange(handleVisibleTimeRangeChange);
        return () => {
          chart.timeScale().unsubscribeVisibleTimeRangeChange(handleVisibleTimeRangeChange);
        };
      }
    };
    t6 = [chart, initialized, onTimeRangeChange];
    $[8] = chart;
    $[9] = initialized;
    $[10] = onTimeRangeChange;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== candlestickSeries || $[14] !== formattedData || $[15] !== volumeSeries) {
    t7 = () => {
      if (candlestickSeries) {
        candlestickSeries.setData(formattedData);
      }
      if (volumeSeries) {
        volumeSeries.setData(getVolumeData(formattedData));
      }
    };
    $[13] = candlestickSeries;
    $[14] = formattedData;
    $[15] = volumeSeries;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  useOnChange(formattedData, t7);
  let t8;
  if ($[17] !== chart || $[18] !== initialTimeRange) {
    t8 = () => {
      if (chart && initialTimeRange) {
        chart.timeScale().setVisibleRange({
          from: initialTimeRange.startTime.getTime() / 1000,
          to: initialTimeRange.endTime.getTime() / 1000
        });
      }
    };
    $[17] = chart;
    $[18] = initialTimeRange;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  useOnChange(initialTimeRange, t8);
  return initialized;
}
function _temp3(data_0) {
  return data_0.map(_temp2);
}
function _temp2(t0) {
  const {
    time,
    volume,
    open,
    close
  } = t0;
  return {
    time,
    value: volume,
    color: close < open ? "#E2475699" : "#47BB6499"
  };
}
function _temp(item) {
  return {
    time: new Date(item.time).getTime() / 1000,
    open: bn(item.open).toNumber(),
    high: bn(item.high).toNumber(),
    low: bn(item.low).toNumber(),
    close: bn(item.close).toNumber(),
    volume: bn(item.volume).toNumber()
  };
}
const tradingViewSafePriceFormatter = price => {
  // TradingView auto-scale emits very very small values instead of 0
  // for the base of the Y axis (e.g. 5e-21)
  if (price < 0.000_000_000_000_000_001) {
    return formatPrice(0);
  }
  return formatPrice(price);
};